import React, { useMemo, useState } from 'react';

import { HeartFilledIcon, StarFilledIcon } from '../../shared/Icons';
import { ScaleRoot } from './scale.styles';
import { RATING_LIMITS, RatingConfig, emojiForPoint, resolveRatingConfig } from './scaleTypes';
import ScaleLabels from './ScaleLabels';
import { ScaleBlockProps, useScaleValue } from './useScaleValue';

const RatingInput = React.forwardRef<HTMLInputElement, ScaleBlockProps<RatingConfig>>(
  (props, ref) => {
    const config = useMemo(
      () => resolveRatingConfig(props.data?.blockConfig),
      [props.data?.blockConfig],
    );
    const { value, commit, interactive, isLiveForm, showError, hiddenInput } = useScaleValue(
      props,
      ref,
      config.autoAdvance,
    );
    const [hovered, setHovered] = useState<number | null>(null);

    const count = Math.min(RATING_LIMITS.max, Math.max(RATING_LIMITS.min, Number(config.count) || 5));
    const selected = Number(value) || 0;
    const highlighted = hovered ?? selected;

    const isActive = (point: number) =>
      config.shape === 'emoji' ? point === highlighted : point <= highlighted;

    return (
      <ScaleRoot className={`scale-block${isLiveForm ? ' is-live' : ''}${showError ? ' has-error' : ''}`}>
        <div className="rating-list" role="radiogroup" onMouseLeave={() => setHovered(null)}>
          {Array.from({ length: count }, (_, index) => {
            const point = index + 1;
            return (
              <button
                key={point}
                type="button"
                role="radio"
                aria-checked={point === selected}
                aria-label={`${point} of ${count}`}
                disabled={!interactive}
                className={`scale-option rating-point is-${config.shape}${
                  isActive(point) ? ' is-active' : ''
                }${point === selected ? ' is-selected' : ''}`}
                onMouseEnter={() => interactive && setHovered(point)}
                onClick={() => commit(String(point), config.autoAdvance)}
              >
                {config.shape === 'star' && <StarFilledIcon />}
                {config.shape === 'heart' && <HeartFilledIcon />}
                {config.shape === 'emoji' && emojiForPoint(index, count)}
              </button>
            );
          })}
        </div>
        <ScaleLabels low={config.lowLabel} high={config.highLabel} />
        {showError && <div className="scale-error-text">This field is required.</div>}
        <input {...hiddenInput} />
      </ScaleRoot>
    );
  },
);

RatingInput.displayName = 'RatingInput';

export default RatingInput;
